// Roadmap Viewer Component - displays the learning roadmap with days and weeks
import React, { useState } from 'react';
import { ChevronRight, CheckCircle, Loader2, Crosshair, Zap, PlayCircle, ChevronDown, ChevronUp, X, Edit2, Save } from 'lucide-react';
import type { RoadmapData, ArticleData } from '../../types';
import { AI_GENERATION_ENDPOINT } from '../../config/apiEndpoints';
import { VideoPlayer } from '../VideoPlayer';
import { ErrorBoundary } from '../ErrorBoundary';
import { generateArticleId, generateManualArticleId } from '../../utils/idGenerator';
import { getArticleUrl } from '../../utils/resourceFallback';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { supabase } from '../../config/supabase';
import { CacheService } from '../../services/cacheService';
import { QuizSection } from '../ai/QuizSection';

interface RoadmapViewerProps {
  roadmap: RoadmapData;
  viewedArticle: ArticleData | null;
  loadingArticle: string | null;
  cachedArticles?: Record<string, ArticleData>;
  adminRoadmapData?: any; // Admin roadmap with flat days data
  onBack: () => void;
  onFetchArticle: (day: any) => Promise<void>;
  onToggleDay: (dayId: string) => void;
  onViewArticle: (article: ArticleData) => void;
  onCompleteDay: (dayId: string) => Promise<void>;
  onLoadNextDay: () => Promise<void>;
  theme: 'light' | 'dark';
  user?: any; // Add user prop for admin check
  isPro?: boolean; // Pro subscription status
  onUpgradeClick?: () => void; // Callback for upgrade button
  roadmapStartDate?: string | null; // Start date of the roadmap for trial calculation
}

export const RoadmapViewer: React.FC<RoadmapViewerProps> = ({
  roadmap,
  viewedArticle,
  loadingArticle,
  cachedArticles = {},
  adminRoadmapData,
  onBack,
  onFetchArticle,
  onToggleDay,
  onViewArticle,
  onCompleteDay,
  onLoadNextDay,
  theme,
  user,
  isPro = false,
  onUpgradeClick,
  roadmapStartDate
}) => {
  const isDark = theme === 'dark';
  const [showHowToModal, setShowHowToModal] = useState(false);
  const [howToSteps, setHowToSteps] = useState<any[]>([]);
  const [loadingHowTo, setLoadingHowTo] = useState(false);
  const [completingDay, setCompletingDay] = useState(false);
  const [loadingNextDay, setLoadingNextDay] = useState(false);
  const [dayCompleted, setDayCompleted] = useState(false);
  
  // Accordion state management
  const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set([0]));
  const [expandedWeeks, setExpandedWeeks] = useState<Map<number, Set<number>>>(new Map([[0, new Set([0])]]));
  
  // Admin edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    title: viewedArticle?.title || '',
    videoId: viewedArticle?.resources?.video?.id || '',
    description: viewedArticle?.subtitle || '',
    deepDive: viewedArticle?.deepDive || '',
    steps: viewedArticle?.steps?.join('\n') || '',
    example: viewedArticle?.example || '',
    keywords: viewedArticle?.keywords?.join(', ') || ''
  });

  const isAdmin = user?.email === 'dynamicnabid@gmail.com';

  // Helper function to get day content from admin roadmap if available
  const getAdminDayContent = (dayNumber: number) => {
    if (!adminRoadmapData?.days) return null;
    return adminRoadmapData.days.find((d: any) => d.day_number === dayNumber);
  };

  // Helper to get video URL from admin roadmap
  const getAdminVideoUrl = (dayNumber: number) => {
    const dayContent = getAdminDayContent(dayNumber);
    if (!dayContent) return undefined;
    if (dayContent.video_url) return dayContent.video_url;
    if (dayContent.video_id) return `https://www.youtube.com/embed/${dayContent.video_id}`;
    return undefined;
  };

  // Accordion toggle handlers
  const toggleMonth = (monthIdx: number) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthIdx)) {
      newExpanded.delete(monthIdx);
    } else {
      newExpanded.add(monthIdx);
    }
    setExpandedMonths(newExpanded);
  };

  const toggleWeek = (monthIdx: number, weekIdx: number) => {
    const newExpandedWeeks = new Map(expandedWeeks);
    if (!newExpandedWeeks.has(monthIdx)) {
      newExpandedWeeks.set(monthIdx, new Set());
    }
    const monthWeeks = newExpandedWeeks.get(monthIdx)!;
    if (monthWeeks.has(weekIdx)) {
      monthWeeks.delete(weekIdx);
    } else {
      monthWeeks.add(weekIdx);
    }
    newExpandedWeeks.set(monthIdx, monthWeeks);
    setExpandedWeeks(newExpandedWeeks);
  };

  const isMonthExpanded = (monthIdx: number) => expandedMonths.has(monthIdx);
  const isWeekExpanded = (monthIdx: number, weekIdx: number) => {
    return expandedWeeks.get(monthIdx)?.has(weekIdx) ?? false;
  };

  const handleEditChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveArticleEdit = async () => {
    if (!viewedArticle?.id) return;
    
    setIsSaving(true);
    try {
      // Parse steps from newline-separated text
      const steps = editForm.steps.split('\n').filter(s => s.trim());

      // Prepare resources object with updated video ID
      const resources = {
        ...(viewedArticle.resources || {}),
        video: {
          ...(viewedArticle.resources?.video || {}),
          id: editForm.videoId
        }
      };

      // Prepare update payload
      const updateData = {
        title: editForm.title,
        subtitle: editForm.description,
        deep_dive: editForm.deepDive,
        steps: steps,
        example: editForm.example,
        resources: resources
      };

      // Update in articles table
      const { error } = await supabase
        .from('articles')
        .update(updateData)
        .eq('id', viewedArticle.id);

      if (error) {
        alert('❌ Error saving article: ' + error.message);
        setIsSaving(false);
        return;
      }

      // Invalidate cache
      CacheService.invalidateCategory('article');

      alert('✅ Article updated successfully!');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving article:', error);
      alert('❌ An error occurred while saving the article');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelArticleEdit = () => {
    setEditForm({
      title: viewedArticle?.title || '',
      videoId: viewedArticle?.resources?.video?.id || '',
      description: viewedArticle?.subtitle || '',
      deepDive: viewedArticle?.deepDive || '',
      steps: viewedArticle?.steps?.join('\n') || '',
      example: viewedArticle?.example || '',
      keywords: viewedArticle?.keywords?.join(', ') || ''
    });
    setIsEditMode(false);
  };

  // Roadmap edit states
  const [isRoadmapEditMode, setIsRoadmapEditMode] = useState(false);
  const [isRoadmapSaving, setIsRoadmapSaving] = useState(false);
  const [roadmapEditForm, setRoadmapEditForm] = useState({
    title: roadmap.title,
    goal: roadmap.goal || roadmap.meta?.goal || '',
    intensity: roadmap.intensity || 'Beginner'
  });

  const handleRoadmapEditChange = (field: string, value: string) => {
    setRoadmapEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveRoadmapEdit = async () => {
    setIsRoadmapSaving(true);
    try {
      // Prepare update payload
      const updateData = {
        title: roadmapEditForm.title,
        goal: roadmapEditForm.goal,
        intensity: roadmapEditForm.intensity,
        updated_at: new Date().toISOString()
      };

      // Update in roadmaps table
      const { error } = await supabase
        .from('roadmaps')
        .update(updateData)
        .eq('id', roadmap.id);

      if (error) {
        alert('❌ Error saving roadmap: ' + error.message);
        setIsRoadmapSaving(false);
        return;
      }

      // Invalidate cache
      CacheService.invalidateCategory('roadmap');

      alert('✅ Roadmap updated successfully!');
      setIsRoadmapEditMode(false);
    } catch (error) {
      console.error('Error saving roadmap:', error);
      alert('❌ An error occurred while saving the roadmap');
    } finally {
      setIsRoadmapSaving(false);
    }
  };

  const handleCancelRoadmapEdit = () => {
    setRoadmapEditForm({
      title: roadmap.title,
      goal: roadmap.goal || roadmap.meta?.goal || '',
      intensity: roadmap.intensity || 'Beginner'
    });
    setIsRoadmapEditMode(false);
  };
  
  // Check if current day is already completed
  React.useEffect(() => {
    if (viewedArticle?.dayId && roadmap.completedDays?.includes(viewedArticle.dayId)) {
      setDayCompleted(true);
    } else {
      setDayCompleted(false);
    }
  }, [viewedArticle?.dayId, roadmap.completedDays]);

  // Extract day number from the roadmap structure using generateArticleId
  const getCurrentDayNumber = () => {
    if (!viewedArticle?.dayId || !roadmap.months || !roadmap.id) return null;
    
    let sequentialDayNumber = 1;
    // Generate proper roadmap ID first
    const roadmapId = roadmap.id; // Use actual roadmap ID, not goal
    
    for (const month of roadmap.months) {
      for (const week of month.weeks || []) {
        for (const day of week.days || []) {
          // Use proper roadmapId to generate dayId
          const dayId = generateArticleId(roadmapId, sequentialDayNumber);
          if (dayId === viewedArticle.dayId) {
            return sequentialDayNumber;
          }
          sequentialDayNumber++;
        }
      }
    }
    return null;
  };

  const currentDayNumber = getCurrentDayNumber();
  const dayNumberText = currentDayNumber ? String(currentDayNumber).padStart(2, '0') : null;
  
  const totalDays = roadmap.months.reduce((acc, month) => {
    return acc + (month.weeks?.reduce((weekAcc, week) => weekAcc + (week.days?.length || 0), 0) || 0);
  }, 0);
  const completedCount = roadmap.completedDays?.length || 0;
  const completedPercentage = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;

  // Function to parse and format markdown-like content
  const parseFormattedContent = (text: string) => {
    const lines = text.split('\n');
    const elements: any[] = [];
    let codeBlock = false;
    let codeContent = '';

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      // Code blocks
      if (trimmed.startsWith('```')) {
        if (codeBlock) {
          elements.push({
            type: 'code',
            content: codeContent,
            key: `code-${idx}`
          });
          codeContent = '';
          codeBlock = false;
        } else {
          codeBlock = true;
        }
        return;
      }

      if (codeBlock) {
        codeContent += (codeContent ? '\n' : '') + line;
        return;
      }

      // Headings
      if (trimmed.startsWith('### ')) {
        elements.push({
          type: 'h3',
          content: trimmed.substring(4),
          key: `h3-${idx}`
        });
      } else if (trimmed.startsWith('## ')) {
        elements.push({
          type: 'h2',
          content: trimmed.substring(3),
          key: `h2-${idx}`
        });
      } else if (trimmed.startsWith('# ')) {
        elements.push({
          type: 'h1',
          content: trimmed.substring(2),
          key: `h1-${idx}`
        });
      }
      // Numbered lists and bullet points
      else if (trimmed.match(/^\d+\.\s/)) {
        const content = trimmed.replace(/^\d+\.\s/, '');
        elements.push({
          type: 'li-numbered',
          content,
          key: `li-${idx}`
        });
      } else if (trimmed.startsWith('- ')) {
        elements.push({
          type: 'li-bullet',
          content: trimmed.substring(2),
          key: `li-${idx}`
        });
      }
      // Bold and italic formatting in paragraphs
      else if (trimmed.length > 0) {
        elements.push({
          type: 'p',
          content: trimmed,
          key: `p-${idx}`
        });
      }
    });

    return elements;
  };

  // Render formatted content
  const renderFormattedContent = (elements: any[]) => {
    return elements.map((el) => {
      switch (el.type) {
        case 'h1':
          return (
            <h2 key={el.key} className={`text-xl sm:text-2xl md:text-3xl font-bold mb-4 mt-6 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
              {el.content}
            </h2>
          );
        case 'h2':
          return (
            <h3 key={el.key} className={`text-lg sm:text-xl md:text-2xl font-bold mb-3 mt-5 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
              {el.content}
            </h3>
          );
        case 'h3':
          return (
            <h4 key={el.key} className={`text-base sm:text-lg md:text-xl font-semibold mb-2 mt-4 ${isDark ? 'text-red-300' : 'text-red-500'}`}>
              {el.content}
            </h4>
          );
        case 'code':
          return (
            <pre key={el.key} className={`p-2 sm:p-4 rounded-lg mb-4 overflow-x-auto text-xs sm:text-sm font-mono max-w-full ${
              isDark ? 'bg-slate-800 text-slate-100' : 'bg-slate-100 text-slate-900'
            }`} style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}>
              <code>{el.content}</code>
            </pre>
          );
        case 'li-numbered':
          return (
            <div key={el.key} className="flex gap-3 mb-2">
              <span className={`flex-shrink-0 font-bold text-xs sm:text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>•</span>
              <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{el.content}</p>
            </div>
          );
        case 'li-bullet':
          return (
            <div key={el.key} className="flex gap-3 mb-2 ml-4">
              <span className={`flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>◦</span>
              <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{el.content}</p>
            </div>
          );
        case 'p':
          return (
            <p key={el.key} className={`mb-3 leading-relaxed text-xs sm:text-sm md:text-base ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {el.content}
            </p>
          );
        default:
          return null;
      }
    });
  };

  // Function to fetch how-to steps from AI
  const fetchHowToSteps = async (projectTitle: string) => {
    setLoadingHowTo(true);
    try {
      const response = await fetch(AI_GENERATION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: `Create a comprehensive, well-formatted step-by-step guide to complete this project effectively: "${projectTitle}".

Format your response with:
- Clear headings (## for main sections, ### for subsections)
- Numbered steps (1. 2. 3. etc)
- Code blocks where applicable (wrap in \`\`\`)
- Sub-bullets for important details (-)
- Bold text for **important** keywords

Structure example:
## Overview
Brief description...

## Prerequisites
1. List requirements

## Step-by-Step Implementation
### Step 1: Description
Details here...

\`\`\`
code example
\`\`\`

### Step 2: Next phase
- Sub point 1
- Sub point 2

Continue with 8-12 comprehensive steps total.`,
          systemPrompt: `You are an expert programming mentor. Create clear, well-structured, and comprehensive step-by-step guides for completing development projects. Use markdown formatting with headings, code blocks, and lists.`,
          jsonMode: false
        })
      });

      if (response.ok) {
        const data = await response.json();
        let content = '';
        
        // Handle different response formats
        if (typeof data.data === 'string') {
          content = data.data;
        } else if (data.data && typeof data.data === 'object') {
          content = JSON.stringify(data.data);
        }
        
        // Parse the formatted content
        const elements = parseFormattedContent(content);
        setHowToSteps(elements.length > 0 ? elements : [{
          type: 'p',
          content: 'Unable to generate formatted steps. Please try again.',
          key: 'error'
        }]);
        setShowHowToModal(true);
      } else {
        setHowToSteps([{
          type: 'p',
          content: 'Error: ' + response.statusText,
          key: 'error'
        }]);
        setShowHowToModal(true);
      }
    } catch (error) {
      console.error('Error fetching how-to steps:', error);
      setHowToSteps([{
        type: 'p',
        content: 'Error generating steps: ' + (error instanceof Error ? error.message : 'Unknown error'),
        key: 'error'
      }]);
      setShowHowToModal(true);
    } finally {
      setLoadingHowTo(false);
    }
  };

  if (viewedArticle) {
    return (
      <div className="animate-in fade-in space-y-6">
        {/* Back Button */}
        <button
          onClick={() => onViewArticle(null as any)}
          className="flex items-center gap-2 text-red-500 font-semibold text-sm group hover:text-red-600 transition-colors"
        >
          <ChevronRight className="rotate-180 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Roadmap
        </button>

        {/* Article Content */}
        <div className={`rounded-3xl p-6 md:p-8 border ${
          isDark ? 'bg-white/[0.03] border-white/5' : 'bg-white border-zinc-300 shadow-xl'
        }`}>
          {/* Header */}
          <div className="mb-6 space-y-2">
            {dayNumberText && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border border-red-400/40 bg-red-500/10 text-red-600 dark:text-red-300 dark:border-red-500/50 dark:bg-red-500/15">
                <span className="text-xs uppercase tracking-wide">Day</span>
                <span className="text-base">{dayNumberText}</span>
              </div>
            )}
            <h3 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{viewedArticle.title}</h3>
            <p className={`text-lg ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{viewedArticle.subtitle}</p>
          </div>

          {/* ADMIN EDIT BUTTON */}
          {isAdmin && !isEditMode && (
            <button
              onClick={() => setIsEditMode(true)}
              className={`mb-6 flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                isDark
                  ? 'bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/30 hover:border-red-500/80'
                  : 'bg-red-100 border border-red-300 text-red-600 hover:bg-red-200 hover:border-red-400'
              }`}
            >
              <Edit2 className="w-4 h-4" />
              Edit Article
            </button>
          )}

          {/* EDIT MODE FORM */}
          {isAdmin && isEditMode && (
            <div className={`mb-8 p-6 rounded-xl border-2 ${
              isDark
                ? 'bg-red-500/5 border-red-500/30'
                : 'bg-red-50/50 border-red-200'
            }`}>
              <h4 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                Edit Article Content
              </h4>
              
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => handleEditChange('title', e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                  />
                </div>

                {/* Video ID */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    YouTube Video ID
                  </label>
                  <input
                    type="text"
                    value={editForm.videoId}
                    onChange={(e) => handleEditChange('videoId', e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                    placeholder="e.g., dQw4w9WgXcQ"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Subtitle/Description
                  </label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => handleEditChange('description', e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                  />
                </div>

                {/* Deep Dive */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Deep Dive (Full Explanation)
                  </label>
                  <textarea
                    value={editForm.deepDive}
                    onChange={(e) => handleEditChange('deepDive', e.target.value)}
                    rows={6}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                  />
                </div>

                {/* Steps */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Learning Steps (one per line)
                  </label>
                  <textarea
                    value={editForm.steps}
                    onChange={(e) => handleEditChange('steps', e.target.value)}
                    rows={4}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none font-mono ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                    placeholder="Step 1&#10;Step 2&#10;Step 3"
                  />
                </div>

                {/* Example */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Example/Code Sample
                  </label>
                  <textarea
                    value={editForm.example}
                    onChange={(e) => handleEditChange('example', e.target.value)}
                    rows={4}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none font-mono ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                  />
                </div>

                {/* Keywords */}
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editForm.keywords}
                    onChange={(e) => handleEditChange('keywords', e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                      isDark
                        ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                    }`}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSaveArticleEdit}
                    disabled={isSaving}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      isDark
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelArticleEdit}
                    disabled={isSaving}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      isDark
                        ? 'bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50'
                        : 'bg-zinc-300 text-zinc-900 hover:bg-zinc-400 disabled:opacity-50'
                    }`}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="mb-8 space-y-4">
            <h4 className={`text-xl font-bold flex items-center gap-2 mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <PlayCircle className="w-5 h-5 text-red-500" />
              Video Tutorial
            </h4>
            <VideoPlayer 
              article={viewedArticle} 
              isDark={isDark}
            />
            {viewedArticle.resources?.video?.url && (
              <a
                href={viewedArticle.resources.video.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block p-4 rounded-lg border-2 transition-all hover:shadow-lg ${
                  isDark
                    ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/60 hover:bg-red-500/15'
                    : 'bg-red-50 border-red-200 hover:border-red-400 hover:bg-red-100/50'
                }`}
              >
                <p className="font-semibold text-red-600 hover:text-red-700 text-sm mb-1">
                  📺 Open in YouTube
                </p>
                <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  Watch the full tutorial and explore related videos
                </p>
              </a>
            )}
          </div>

          {/* Content */}
          <div className={`prose prose-lg max-w-none mb-8 ${isDark ? 'prose-invert' : ''}`}>
            {/* Overview */}
            <h4 className={`text-xl font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Crosshair className="w-5 h-5 text-red-500" />
              Overview
            </h4>
            <p className={`text-base leading-relaxed mb-6 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{viewedArticle.deepDive}</p>

            {/* Technical Concepts */}
            {viewedArticle.technicalConcepts && viewedArticle.technicalConcepts.length > 0 && (
              <>
                <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  <Zap className="w-5 h-5 text-red-500" />
                  Key Concepts
                </h4>
                <div className="grid gap-3 mb-6">
                  {viewedArticle.technicalConcepts?.map((concept, idx) => (
                    <div key={idx} className={`p-4 rounded-xl border ${
                      isDark ? 'bg-white/5 border-white/10' : 'bg-zinc-50 border-zinc-200'
                    }`}>
                      <strong className="text-red-500 font-semibold">{concept.term}:</strong> <span className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{concept.explanation}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Steps */}
            {viewedArticle.steps && viewedArticle.steps.length > 0 && (
              <>
                <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  <Crosshair className="w-5 h-5 text-red-500" />
                  Learning Steps
                </h4>
                <ol className="space-y-2 mb-6">
                  {viewedArticle.steps?.map((step, idx) => (
                    <li key={idx} className={`flex gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-white/5' : 'bg-zinc-50'
                    }`}>
                      <span className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{step}</span>
                    </li>
                  ))}
                </ol>
              </>
            )}

            {/* Example Section */}
            {viewedArticle.example && (
              <>
                <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  <Zap className="w-5 h-5 text-red-500" />
                  {viewedArticle.exampleType === 'code' ? 'Code Example' : 'Real-Life Example'}
                </h4>
                <div className={`p-4 rounded-xl border ${
                  isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-zinc-900 border-zinc-700'
                }`}>
                  {viewedArticle.exampleType === 'code' ? (
                    <pre className={`text-sm overflow-x-auto ${isDark ? 'text-zinc-100' : 'text-zinc-100'}`}>
                      <code>{viewedArticle.example}</code>
                    </pre>
                  ) : (
                    <p className={isDark ? 'text-zinc-100' : 'text-zinc-100'}>{viewedArticle.example}</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Quiz Section */}
          {viewedArticle.quiz && (
            <QuizSection
              quiz={viewedArticle.quiz}
              articleId={viewedArticle.id || ''}
              articleTitle={viewedArticle.title}
              userId={user?.id || null}
              isDark={isDark}
              isPro={isPro}
              onUpgradeClick={onUpgradeClick}
              roadmapStartDate={roadmapStartDate}
            />
          )}

          {/* Learning Resources Section - Fallback if no quiz */}
          {!viewedArticle.quiz && viewedArticle.resources && (
            <div className={`rounded-2xl p-6 border-2 border-red-500/30 ${
              isDark ? 'bg-red-500/5' : 'bg-red-50/50'
            }`}>
              <h4 className={`text-xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                <PlayCircle className="w-5 h-5" />
                Learning Resources
              </h4>

              <div className="space-y-6">
                {/* Articles */}
                {viewedArticle.resources.articles && viewedArticle.resources.articles.length > 0 && (
                  <div>
                    <h5 className={`text-sm font-semibold mb-3 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                      📄 High-Quality Articles
                    </h5>
                    <div className="space-y-3">
                      {viewedArticle.resources.articles.map((article, idx) => {
                        const { url, isFallback } = getArticleUrl(
                          article.url,
                          article.title,
                          viewedArticle.topic,
                          roadmap.goal
                        );
                        return (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block p-4 rounded-lg border transition-all hover:shadow-lg ${
                              isFallback
                                ? isDark
                                  ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50 hover:bg-amber-500/10'
                                  : 'bg-amber-50 border-amber-200 hover:border-amber-300 hover:bg-amber-100/30'
                                : isDark
                                  ? 'bg-white/5 border-white/10 hover:border-red-500/50 hover:bg-white/10'
                                  : 'bg-white border-zinc-200 hover:border-red-300 hover:bg-red-50/30'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className={`font-semibold text-sm break-words flex-1 ${
                                isFallback
                                  ? isDark ? 'text-amber-400' : 'text-amber-600'
                                  : 'text-red-600 hover:text-red-700'
                              }`}>
                                {article.title}
                              </p>
                              {isFallback && (
                                <span className={`text-xs px-2 py-1 rounded font-semibold whitespace-nowrap ${
                                  isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-200 text-amber-800'
                                }`}>
                                  Search
                                </span>
                              )}
                            </div>
                            {article.preview && (
                              <p className={`text-xs leading-relaxed mb-2 break-words ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                                {article.preview}
                              </p>
                            )}
                            <p className={`text-xs break-all ${
                              isFallback
                                ? isDark ? 'text-amber-500/70' : 'text-amber-600/70'
                                : isDark ? 'text-zinc-500' : 'text-zinc-400'
                            }`}>
                              🔗 {isFallback ? 'Google Search →' : ''} {url.substring(0, 60)}...
                            </p>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-white/10">
            <button
              onClick={async () => {
                if (dayCompleted) return; // Prevent re-clicking if already completed
                setCompletingDay(true);
                try {
                  if (viewedArticle?.dayId) {
                    await onCompleteDay(viewedArticle.dayId);
                    // Set completed state after successful completion
                    setDayCompleted(true);
                  }
                } finally {
                  setCompletingDay(false);
                }
              }}
              disabled={completingDay || loadingNextDay || dayCompleted}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                dayCompleted
                  ? isDark
                    ? 'bg-green-500/30 border border-green-400 text-green-300 scale-105 shadow-lg shadow-green-500/20'
                    : 'bg-green-200/60 border border-green-400 text-green-800 scale-105 shadow-lg shadow-green-400/20'
                  : isDark
                    ? 'bg-green-600/20 border border-green-500/50 text-green-400 hover:bg-green-600/30 hover:border-green-500/80 disabled:opacity-50'
                    : 'bg-green-100 border border-green-300 text-green-700 hover:bg-green-200 hover:border-green-400 disabled:opacity-50'
              }`}
            >
              {dayCompleted ? (
                <div className="flex items-center justify-center gap-2 animate-in slide-in-from-left">
                  <CheckCircle className="w-5 h-5 animate-pulse" />
                  <span>✨ You have completed {dayNumberText ? `Day ${dayNumberText}` : 'this lesson'}</span>
                </div>
              ) : completingDay ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Marking as completed...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Mark as completed {dayNumberText ? `Day ${dayNumberText}` : 'this lesson'}
                </>
              )}
            </button>

            <button
              onClick={async () => {
                setLoadingNextDay(true);
                try {
                  await onLoadNextDay();
                } finally {
                  setLoadingNextDay(false);
                }
              }}
              disabled={completingDay || loadingNextDay}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                isDark
                  ? 'bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/30 hover:border-red-500/80 disabled:opacity-50'
                  : 'bg-red-100 border border-red-300 text-red-700 hover:bg-red-200 hover:border-red-400 disabled:opacity-50'
              }`}
            >
              {loadingNextDay ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading Next Day...
                </>
              ) : (
                <>
                  Next Article
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* How-To Modal */}
          {showHowToModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
              <div className={`rounded-xl sm:rounded-2xl max-w-full sm:max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl border text-sm sm:text-base ${
                isDark 
                  ? 'bg-slate-900 border-slate-700' 
                  : 'bg-white border-slate-200'
              }`}>
                {/* Modal Header */}
                <div className={`sticky top-0 flex items-center justify-between p-3 sm:p-6 border-b gap-2 ${
                  isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
                }`}>
                  <h3 className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Step-by-Step Guide</h3>
                  <button
                    onClick={() => setShowHowToModal(false)}
                    className={`p-2 rounded-lg transition-all flex-shrink-0 ${
                      isDark 
                        ? 'hover:bg-slate-700 text-slate-400' 
                        : 'hover:bg-slate-100 text-slate-600'
                    }`}
                  >
                    <X className="w-5 sm:w-6 h-5 sm:h-6" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className={`p-3 sm:p-6 space-y-3 sm:space-y-4 overflow-y-auto ${isDark ? 'bg-slate-800/30' : 'bg-slate-50'}`}>
                  {renderFormattedContent(howToSteps)}
                </div>

                {/* Modal Footer */}
                <div className={`sticky bottom-0 p-3 sm:p-6 border-t ${
                  isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
                }`}>
                  <button
                    onClick={() => setShowHowToModal(false)}
                    className={`w-full px-4 py-3 sm:py-4 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                      isDark
                        ? 'bg-red-600 hover:bg-red-700 text-white'
                        : 'bg-red-500 hover:bg-red-600 text-white'
                    }`}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in space-y-8 md:space-y-12">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 md:gap-8">
        <div className="space-y-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-red-500 font-semibold text-sm group hover:text-red-600 transition-colors"
          >
            <ChevronRight className="rotate-180 w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <div className="space-y-3">
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 bg-clip-text text-transparent drop-shadow-lg inline-block">
                {roadmap.title}
              </span>
            </h2>
            <div className={`h-1.5 w-24 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-600`} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
              isDark 
                ? 'bg-gradient-to-r from-red-500/30 to-red-600/30 text-red-300 border border-red-500/30 hover:border-red-500/50' 
                : 'bg-gradient-to-r from-red-100 to-red-50 text-red-700 border border-red-300 hover:border-red-400 shadow-sm'
            }`}>
              {roadmap.intensity}
            </span>
            <span className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
              isDark 
                ? 'bg-white/10 text-zinc-300 border border-white/20 hover:border-white/30' 
                : 'bg-zinc-100 text-zinc-700 border border-zinc-300 hover:border-zinc-400 shadow-sm'
            }`}>
              {roadmap.lang}
            </span>
            <span className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
              isDark 
                ? 'bg-white/10 text-zinc-300 border border-white/20 hover:border-white/30' 
                : 'bg-zinc-100 text-zinc-700 border border-zinc-300 hover:border-zinc-400 shadow-sm'
            }`}>
              {roadmap.meta?.studyTime}h/day
            </span>
          </div>
        </div>

        {/* ADMIN EDIT BUTTON FOR ROADMAP */}
        {isAdmin && !isRoadmapEditMode && (
          <button
            onClick={() => setIsRoadmapEditMode(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              isDark
                ? 'bg-red-600/20 border border-red-500/50 text-red-400 hover:bg-red-600/30 hover:border-red-500/80'
                : 'bg-red-100 border border-red-300 text-red-600 hover:bg-red-200 hover:border-red-400'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            Edit Roadmap
          </button>
        )}

        {/* ROADMAP EDIT MODE FORM */}
        {isAdmin && isRoadmapEditMode && (
          <div className={`p-6 rounded-xl border-2 ${
            isDark
              ? 'bg-red-500/5 border-red-500/30'
              : 'bg-red-50/50 border-red-200'
          }`}>
            <h4 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              Edit Roadmap Details
            </h4>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Roadmap Title
                </label>
                <input
                  type="text"
                  value={roadmapEditForm.title}
                  onChange={(e) => handleRoadmapEditChange('title', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Goal */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Learning Goal
                </label>
                <textarea
                  value={roadmapEditForm.goal}
                  onChange={(e) => handleRoadmapEditChange('goal', e.target.value)}
                  rows={4}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm resize-none ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                />
              </div>

              {/* Intensity */}
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  Intensity Level
                </label>
                <select
                  value={roadmapEditForm.intensity}
                  onChange={(e) => handleRoadmapEditChange('intensity', e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-700 text-white focus:border-red-500 focus:outline-none'
                      : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
                  }`}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSaveRoadmapEdit}
                  disabled={isRoadmapSaving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                    isDark
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                  }`}
                >
                  {isRoadmapSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelRoadmapEdit}
                  disabled={isRoadmapSaving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                    isDark
                      ? 'bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50'
                      : 'bg-zinc-300 text-zinc-900 hover:bg-zinc-400 disabled:opacity-50'
                  }`}
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Card */}
        <div className={`p-6 rounded-3xl border flex items-center gap-6 shadow-xl ${
          isDark ? 'bg-white/[0.03] border-white/5' : 'bg-white border-zinc-200/80'
        }`}>
          <div className="text-right">
            <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              Progress
            </p>
            <p className="text-3xl font-bold text-red-500 leading-none">
              {completedCount}
            </p>
            <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
              of {totalDays} days
            </p>
          </div>
          <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30">
            <Crosshair className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className={`p-6 rounded-3xl border ${
        isDark ? 'bg-white/[0.03] border-white/5' : 'bg-white border-zinc-200/80 shadow-lg'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Overall Progress</h3>
          <span className="text-2xl font-bold text-red-500">{completedPercentage}%</span>
        </div>
        <div className={`w-full h-3 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-zinc-100'}`}>
          <div
            className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full transition-all duration-700"
            style={{ width: `${completedPercentage}%` }}
          />
        </div>
      </div>

      {/* Months and Weeks - Accordion Structure */}
      {roadmap.months?.map((month: any, monthIdx: number) => (
        <div key={monthIdx} className="space-y-0">
          {/* Month Header - Clickable */}
          <button
            onClick={() => toggleMonth(monthIdx)}
            className={`w-full flex items-center gap-6 p-4 md:p-6 rounded-2xl transition-all hover:bg-opacity-50 ${
              isMonthExpanded(monthIdx)
                ? isDark
                  ? 'bg-white/5 border border-white/10'
                  : 'bg-zinc-50 border border-zinc-200'
                : isDark
                ? 'bg-white/[0.02] border border-white/5 hover:bg-white/5'
                : 'bg-white border border-zinc-100 hover:bg-zinc-50'
            }`}
          >
            <span className={`text-7xl font-bold italic ${isDark ? 'text-white/5' : 'text-zinc-100'} shrink-0`}>
              {monthIdx + 1}
            </span>
            <div className="flex-1 text-left">
              <h3 className={`text-3xl md:text-4xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-zinc-900'}`}>{month.name}</h3>
              <div className={`h-1 w-24 bg-gradient-to-r from-red-500 to-rose-500 rounded-full mt-2`} />
            </div>
            <ChevronDown
              className={`w-6 h-6 transition-transform duration-300 shrink-0 ${
                isMonthExpanded(monthIdx) ? 'rotate-180' : ''
              } ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
            />
          </button>

          {/* Weeks - Conditional Rendering */}
          {isMonthExpanded(monthIdx) && (
            <div className="space-y-8 pt-6 pl-4 md:pl-6 border-l-2 border-white/5">
              {month.weeks?.map((week: any, weekIdx: number) => (
                <div key={weekIdx} className="space-y-0">
                  {/* Week Header - Clickable */}
                  <button
                    onClick={() => toggleWeek(monthIdx, weekIdx)}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl transition-all hover:bg-opacity-50 ${
                      isWeekExpanded(monthIdx, weekIdx)
                        ? isDark
                          ? 'bg-white/5 border border-white/10'
                          : 'bg-zinc-50 border border-zinc-200'
                        : isDark
                        ? 'bg-white/[0.02] border border-white/5 hover:bg-white/5'
                        : 'bg-white border border-zinc-100 hover:bg-zinc-50'
                    }`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30 shrink-0">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h4 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-zinc-900'}`}>{week.name}</h4>
                      <p className={`text-lg ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{week.goal}</p>
                    </div>
                    <ChevronDown
                      className={`w-6 h-6 transition-transform duration-300 shrink-0 ${
                        isWeekExpanded(monthIdx, weekIdx) ? 'rotate-180' : ''
                      } ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
                    />
                  </button>

                  {/* Days Grid - Conditional Rendering */}
                  {isWeekExpanded(monthIdx, weekIdx) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 pt-4">
                      {week.days?.map((day: any, dayIdx: number) => {
                        // Calculate sequential day number across entire roadmap
                        let sequentialDayNumber = 1;
                        outerLoop:
                        for (const m of roadmap.months || []) {
                          for (const w of m.weeks || []) {
                            for (const d of w.days || []) {
                              if (d.topic === day.topic && d.task === day.task) {
                                break outerLoop;
                              }
                              sequentialDayNumber++;
                            }
                          }
                        }
                        
                        const dayId = roadmap.id ? generateArticleId(
                          roadmap.id,
                          sequentialDayNumber
                        ) : `day-${sequentialDayNumber}`;
                        // Cache key is same as dayId (article_id)
                        const cacheKey = dayId;
                        const isCached = cachedArticles[cacheKey] !== undefined;
                        const isCompleted = roadmap.completedDays?.includes(dayId);
                        const isLoading = loadingArticle === dayId;
                        
                        // Debug logging for cache status
                        if (dayIdx === 0 && weekIdx === 0 && monthIdx === 0) {
                          console.log('🔍 First day card cache check:', {
                            dayTopic: day.topic,
                            sequentialDayNumber,
                            dayId,
                            cacheKey,
                            isCached,
                            totalCachedArticles: Object.keys(cachedArticles).length,
                            cachedKeys: Object.keys(cachedArticles).slice(0, 3)
                          });
                        }

                        return (
                          <div
                            key={dayIdx}
                            role="button"
                            tabIndex={0}
                            onClick={async () => {
                              await onFetchArticle(day);
                            }}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                await onFetchArticle(day);
                              }
                            }}
                            aria-disabled={isLoading}
                            className={`group relative p-5 md:p-6 rounded-2xl border text-left transition-all card-lift overflow-hidden ${
                              isCompleted
                                ? isDark
                                  ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50'
                                  : 'bg-red-50 border-red-300 hover:border-red-400'
                                : isDark
                                ? 'bg-white/[0.03] border-white/5 hover:bg-white/[0.06] hover:border-red-500/30'
                                : 'bg-white border-zinc-200/80 hover:border-red-300 hover:shadow-xl'
                            } ${isLoading ? 'opacity-50 pointer-events-none' : ''} before:absolute before:inset-0 before:rounded-2xl before:opacity-0 group-hover:before:opacity-100 before:transition-opacity before:pointer-events-none ${
                              isCompleted 
                                ? isDark 
                                  ? 'before:bg-gradient-to-br before:from-red-500/10 before:to-transparent'
                                  : 'before:bg-gradient-to-br before:from-red-400/5 before:to-transparent'
                                : isDark
                                ? 'before:bg-gradient-to-br before:from-red-500/5 before:via-orange-500/5 before:to-transparent'
                                : 'before:bg-gradient-to-br before:from-red-400/10 before:via-orange-400/5 before:to-transparent'
                            }`}
                          >
                            {/* Premium accent line */}
                            <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r transition-all ${
                              isCompleted
                                ? 'from-red-500 via-red-400 to-transparent'
                                : 'from-amber-400 via-orange-500 to-transparent group-hover:via-orange-600'
                            }`} />

                            {/* Relative content wrapper */}
                            <div className="relative">
                              {/* Day Header */}
                              <div className="flex items-start justify-between gap-3 mb-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                    isCompleted
                                      ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                                      : isDark
                                      ? 'bg-gradient-to-r from-amber-400/20 to-orange-500/20 text-amber-300 border border-amber-500/30'
                                      : 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border border-amber-300'
                                  }`}>
                                    Day {sequentialDayNumber}
                                  </span>
                                  {isCompleted && (
                                    <div className="w-6 h-6 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/40">
                                      <CheckCircle className="w-3.5 h-3.5 text-white animate-pulse" />
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleDay(dayId);
                                  }}
                                  className={`w-7 h-7 rounded-lg border transition-all flex items-center justify-center ${
                                    isCompleted
                                      ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/30'
                                      : isDark
                                      ? 'border-white/20 text-zinc-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10'
                                      : 'border-zinc-300 text-zinc-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50'
                                  }`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {/* Content */}
                              <div className="space-y-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h5 className={`font-bold text-lg leading-tight ${isDark ? 'text-white' : 'text-zinc-900'} group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors`}>{day.topic}</h5>
                                  {isCached && (
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                                      isDark 
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                        : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                    }`}>
                                      <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse"></span>
                                      Saved
                                    </span>
                                  )}
                                </div>
                                {day.weekGoal && (
                                  <p className={`text-xs font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                                    {day.weekGoal}
                                  </p>
                                )}
                                <p className={`text-sm leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'} line-clamp-3 group-hover:text-opacity-90 transition-all`}>
                                  {day.task}
                                </p>
                              </div>

                              {/* Loading State */}
                              {isLoading && (
                                <div className="mt-4">
                                  <LoadingSpinner 
                                    context="article" 
                                    message="Loading article..." 
                                    size="sm"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

    </div>
  );
};
